/**
 * account-deletion-api.ts — self-service account deletion.
 *
 * DELETE /me                → 202 {"scheduled_deletion_at": "<RFC3339>"}
 *                             400 wrong confirmation text
 *                             409 owner_must_transfer
 * POST /me/cancel-deletion  → 200, cancels a scheduled deletion
 *
 * Recovery is deliberately NOT a magic link — a stolen inbox must not be able
 * to resurrect an account — so the cancel path is reached by signing in
 * normally, which is why `pending_deletion` rides along on sign-in responses.
 */
import { apiClient, getErrorCode } from './index';

/**
 * The word the user must type. Lives next to the request that sends it so the
 * UI gate and the wire value cannot drift apart.
 */
export const DELETE_CONFIRMATION = 'delete';

/**
 * 409 code meaning the caller still owns a workspace with other members in it.
 * A refusal with a fix, not a failure — the UI must not render it red.
 */
export const OWNER_MUST_TRANSFER = 'owner_must_transfer';

/** Days between scheduling a deletion and it completing. */
export const DELETION_GRACE_DAYS = 30;

export interface ScheduledDeletion {
    scheduled_deletion_at: string;
}

/** What a sign-in response carries while the account is inside its window. */
export interface PendingDeletion {
    scheduled_deletion_at: string;
}

export async function requestAccountDeletion(): Promise<ScheduledDeletion> {
    // Axios sends a DELETE body only under `data` — passing it positionally
    // (as with post) makes it the config object and drops the payload, which
    // the backend would answer 400 for.
    const { data } = await apiClient.delete<ScheduledDeletion>('/me', {
        data: { confirm: DELETE_CONFIRMATION },
    });
    return data;
}

export async function cancelAccountDeletion(): Promise<void> {
    await apiClient.post('/me/cancel-deletion');
}

export function isOwnerMustTransfer(error: unknown): boolean {
    return getErrorCode(error) === OWNER_MUST_TRANSFER;
}

/**
 * Formats an RFC3339 deletion timestamp for display. Falls back to the raw
 * string rather than hiding the date: someone deciding whether to cancel needs
 * the day more than they need it neat.
 */
export function formatDeletionDate(rfc3339: string): string {
    const date = new Date(rfc3339);
    if (Number.isNaN(date.getTime())) return rfc3339;
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}
